// Generates a self-contained SVG "photo" tile for a product so the catalogue is
// never blank. Used as a fallback when the backend has no real, loadable image
// (it currently stores bare filenames like "apples.jpg" that 404). If a product
// ever gets a real http(s) image URL, that is used instead and this is ignored.

const EMOJI: Array<[RegExp, string]> = [
  [/apple/i, "🍎"], [/banana/i, "🍌"], [/orange|citrus|lemon|lime/i, "🍊"],
  [/berry|berries|blueberr|strawberr/i, "🫐"], [/grape/i, "🍇"], [/mango|tropical|pineapple/i, "🥭"],
  [/tomato/i, "🍅"], [/onion/i, "🧅"], [/carrot|root/i, "🥕"], [/potato/i, "🥔"],
  [/spinach|leafy|green|lettuce|kale/i, "🥬"], [/broccoli|cruciferous|cauliflower/i, "🥦"],
  [/pepper|capsicum/i, "🫑"], [/corn/i, "🌽"], [/cucumber|vegetable/i, "🥒"],
  [/milk/i, "🥛"], [/cheese/i, "🧀"], [/egg/i, "🥚"], [/butter|margarine/i, "🧈"], [/yogurt|yoghurt/i, "🥛"],
  [/bread/i, "🍞"], [/cake|pastry|pastries/i, "🧁"], [/cookie|biscuit/i, "🍪"], [/bakery|croissant|bun/i, "🥐"],
  [/beef|steak|meat|lamb|pork/i, "🥩"], [/chicken|poultry/i, "🍗"], [/fish|seafood|prawn|shrimp/i, "🐟"],
  [/rice/i, "🍚"], [/oil|olive/i, "🫒"], [/pasta|noodle/i, "🍝"], [/flour|grain|pantry|staple/i, "🌾"],
  [/juice|beverage|drink|soda/i, "🧃"], [/coffee/i, "☕"], [/tea/i, "🍵"], [/water/i, "💧"],
  [/frozen|ice/i, "🧊"], [/snack|chip|confection|candy|chocolate/i, "🍿"],
  [/baby|kid|infant/i, "🍼"], [/clean|detergent|soap|household|personal/i, "🧴"],
  [/dairy/i, "🥛"], [/produce|fruit/i, "🍎"],
];

function emojiFor(name: string, category?: string): string {
  const hay = `${name} ${category ?? ""}`;
  for (const [re, e] of EMOJI) if (re.test(hay)) return e;
  return "🛒";
}

// Deterministic soft background tint from the text, so a product always looks the same.
function tint(text: string): { from: string; to: string } {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) % 360;
  return { from: `hsl(${h}, 55%, 93%)`, to: `hsl(${(h + 40) % 360}, 50%, 86%)` };
}

function escapeXml(s: string): string {
  return s.replace(/[<>&'"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[c] as string));
}

/** A data-URI SVG tile for the product. Always loadable, works offline. */
export function foodImageDataUri(name: string, category?: string): string {
  const { from, to } = tint(name || category || "product");
  const emoji = emojiFor(name || "", category);
  const label = escapeXml((name || "").slice(0, 28));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
  <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>
  </linearGradient></defs>
  <rect width="400" height="400" fill="url(#g)"/>
  <text x="200" y="205" font-size="160" text-anchor="middle" dominant-baseline="central">${emoji}</text>
  <text x="200" y="350" font-size="26" font-family="Arial, Helvetica, sans-serif" font-weight="600"
    fill="#2f4f41" text-anchor="middle">${label}</text>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// --- real photo by keyword -------------------------------------------------
// Sources a real product photo by search term from a public image endpoint
// (loads cross-origin as an <img>, no API key). If it fails, the caller falls
// back to foodImageDataUri so a tile is always shown.
export function productPhotoUrl(name: string, _id: number, category?: string): string {
  const q = `${name} ${category ?? ""}`.trim() || "food product";
  return `https://tse.mm.bing.net/th?q=${encodeURIComponent(q)}&w=400&h=400&c=7`;
}
